import { create } from 'zustand'
import type { EmailThreadSummary, EmailMessage } from '../types'
import { gmailService } from '../services/gmail'

interface CacheEntry<T> {
  data: T
  timestamp: number
}

const CACHE_TTL = 5 * 60 * 1000

interface GmailState {
  threadCache: Map<string, CacheEntry<EmailThreadSummary[]>>
  messageCache: Map<string, CacheEntry<EmailMessage[]>>
  loadingContacts: Set<string>
  loadingThreads: Set<string>
  error: string | null

  fetchThreadsForContact: (contactEmail: string) => Promise<EmailThreadSummary[]>
  fetchFullThread: (threadId: string) => Promise<EmailMessage[]>
  clearCache: () => void
  clearContactCache: (contactEmail: string) => void
}

export const useGmailStore = create<GmailState>((set, get) => ({
  threadCache: new Map(),
  messageCache: new Map(),
  loadingContacts: new Set(),
  loadingThreads: new Set(),
  error: null,

  fetchThreadsForContact: async (contactEmail: string) => {
    const normalised = contactEmail.toLowerCase().trim()
    const { threadCache, loadingContacts } = get()

    const cached = threadCache.get(normalised)
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      return cached.data
    }

    if (loadingContacts.has(normalised)) {
      return cached?.data || []
    }

    set({ 
      loadingContacts: new Set([...loadingContacts, normalised]),
      error: null 
    })

    try {
      const threads = await gmailService.fetchThreadsForEmail(normalised)
      
      const newCache = new Map(get().threadCache)
      newCache.set(normalised, { data: threads, timestamp: Date.now() })
      
      const newLoading = new Set(get().loadingContacts)
      newLoading.delete(normalised)
      
      set({ 
        threadCache: newCache,
        loadingContacts: newLoading 
      })

      return threads
    } catch (err) {
      const newLoading = new Set(get().loadingContacts)
      newLoading.delete(normalised)
      
      set({ 
        loadingContacts: newLoading,
        error: err instanceof Error ? err.message : 'Failed to fetch emails'
      })

      return cached?.data || []
    }
  },

  fetchFullThread: async (threadId: string) => {
    const { messageCache, loadingThreads } = get()

    const cached = messageCache.get(threadId)
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      return cached.data
    }

    if (loadingThreads.has(threadId)) {
      return cached?.data || []
    }

    set({ 
      loadingThreads: new Set([...loadingThreads, threadId]),
      error: null 
    })

    try {
      const messages = await gmailService.getFullThread(threadId)
      
      const newCache = new Map(get().messageCache)
      newCache.set(threadId, { data: messages, timestamp: Date.now() })
      
      const newLoading = new Set(get().loadingThreads)
      newLoading.delete(threadId)
      
      set({ 
        messageCache: newCache,
        loadingThreads: newLoading 
      })

      return messages
    } catch (err) {
      const newLoading = new Set(get().loadingThreads)
      newLoading.delete(threadId)
      
      set({ 
        loadingThreads: newLoading,
        error: err instanceof Error ? err.message : 'Failed to fetch thread'
      })

      return cached?.data || []
    }
  },

  clearCache: () => {
    set({
      threadCache: new Map(),
      messageCache: new Map(),
      loadingContacts: new Set(),
      loadingThreads: new Set(),
      error: null,
    })
  },

  clearContactCache: (contactEmail: string) => {
    const normalised = contactEmail.toLowerCase().trim()
    const newCache = new Map(get().threadCache)
    newCache.delete(normalised)
    set({ threadCache: newCache })
  },
}))
